import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import Toast from 'react-native-toast-message';
import { useDispatch, useSelector } from 'react-redux';

import {
	BronzeButton,
	BronzeTournamentCard,
	GoldButton,
	GoldTournamentCard,
	SilverTournamentCard,
	WinButton,
} from '../../../assets';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import {
	tournamentsListRefreshRequest,
	tournamentsSubscribe,
	tournamentsUnregisterRequest,
	tournamentsUnsubscribe,
} from '../../../store/tournaments/tournamentsActions';
import {
	selectTournaments,
	selectTournamentsListLoading,
} from '../../../store/tournaments/tournamentsReducer';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { CarouselNav } from '../common/CarouselNav';
import { STAGE, useCarouselStage } from '../common/carouselStage';
import { getDesktopColumns } from '../Desktop/DesktopBottomBars';
import { DesktopCoverflow } from '../Desktop/DesktopCoverflow';
import { DesktopJackpotBar } from '../Desktop/DesktopJackpotBar';
import { useDesktopLayout } from '../Desktop/desktopLayout';
import { DesktopRecentTablesBar } from '../Desktop/DesktopRecentTablesBar';
import RegisteredTournamentsPanel from '../Tournaments/RegisteredTournamentsPanel';
import TournamentInfoModal from '../Tournaments/TournamentInfoModal';
import { tournamentTitle } from '../Tournaments/tournamentModalTheme';
import {
	TournamentsCarousel,
	TournamentsCarouselHandle,
	TournamentSlide,
} from './TournamentsCarousel';

export const TROPHIES: Record<string, any> = {
	bronze: BronzeTournamentCard,
	silver: SilverTournamentCard,
	gold: GoldTournamentCard,
};
export const WIN_BUTTONS: Record<string, any> = {
	bronze: BronzeButton,
	silver: WinButton,
	gold: GoldButton,
};

export type SnG = {
	id: number;
	entry_icon: 'bronze' | 'silver' | 'gold';
	entry_level: number;
	winAmount: string;
	entry_size: string;
	users: string[];
	active_tables: number;
	players: number;
	prices: { [key: number]: number };
	registered?: boolean;
	roomId: string;
	start_in: Date | null;
	close_at: Date | null;
	tournament_chips: number;
};

export type TournamentProps = {
	sng: SnG;
	trophySource: any;
	winButtonSource: any;
	index: number;
	totalCount: number;
	isSpecial?: boolean;
	itemWidth: number;
	itemHeight?: number;
	showNoUserWarning?: boolean;
	showLevelLockWarning?: boolean;
	loginWarningText?: string;
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
// The desktop coverflow's card keeps the original 226x416 proportions.
const PORTRAIT_CARD_ASPECT = 416 / 226;
// "Registered Tournaments" on the stage (Figma 8526:39813): 361x221 at 523, from the dots down to
// the stage's bottom. Its rows scroll inside it.
const PANEL_TOP = 523;
const PANEL_WIDTH = 361;
const PANEL_HEIGHT = 221;
// Room around the front card for its glow, which the carousel would otherwise clip.
const CARD_GLOW_ROOM = 16;
// The desktop page is the VIP rooms' (VipRoomsScreen): the desktop home's 592-high block, the
// bottom bars at its 510, the cards 420 high from 8 down. The registered panel stands beside the
// front card at its height, its rows scrolling inside it.
const DESKTOP_BLOCK_HEIGHT = 592;
const DESKTOP_BARS_TOP = 510;
const DESKTOP_CARD_HEIGHT = 420;
const DESKTOP_TOP_GAP = 8;

type Nav = NativeStackNavigationProp<RootStackParamList>;

export const TournamentsSection = () => {
	const dispatch = useDispatch();
	const navigation = useNavigation<Nav>();
	const authToken = useSelector((state: RootState) => state.auth?.token ?? null);
	const authUserId = useSelector((state: RootState) => state.auth?.user?.id ?? null);
	const isLogged = useSelector(selectIsLogged);
	const isAuthIdentityPending = Boolean(authToken) && !authUserId;

	useEffect(() => {
		if (isAuthIdentityPending) {
			dispatch(tournamentsUnsubscribe());
			return;
		}

		dispatch(tournamentsSubscribe());

		return () => {
			dispatch(tournamentsUnsubscribe());
		};
	}, [dispatch, isAuthIdentityPending, authUserId, isLogged]);

	const tournaments = useSelector(selectTournaments);
	const loading = useSelector(selectTournamentsListLoading);
	const [initialLoadDone, setInitialLoadDone] = useState(false);
	useEffect(() => {
		if (loading) setInitialLoadDone(true);
	}, [loading]);
	const { isDesktop } = useDevice();
	const stage = useCarouselStage();
	const desktop = useDesktopLayout();
	const carouselRef = useRef<TournamentsCarouselHandle>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const carouselProgress = useSharedValue(0);
	// The tournament whose info modal is open - from a card tap or a registered row. Kept by id so
	// the modal follows the live list item as registrations change.
	const [infoTournamentId, setInfoTournamentId] = useState<string | null>(null);
	const openTournament = useCallback((sng: SnG) => setInfoTournamentId(String(sng.id)), []);
	const unregisterTournament = useCallback(
		(sng: SnG) => {
			dispatch(tournamentsUnregisterRequest({ tournamentId: String(sng.id) }));
			Toast.show({
				type: 'success',
				text1: t('T_TOURNAMENT_UNREGISTER'),
				visibilityTime: 3200,
				position: 'bottom',
			});
		},
		[dispatch],
	);

	// The poll reads the latest list from a ref so socket-driven list updates
	// (new array identity on every getSnGLobbyUpdate) don't tear down and
	// recreate the interval.
	const tournamentsRef = useRef(tournaments);
	useEffect(() => {
		tournamentsRef.current = tournaments;
	}, [tournaments]);

	useEffect(() => {
		const interval = setInterval(() => {
			const current = tournamentsRef.current;
			if (!current.length) return;
			const hasExpired = current.some(s => {
				const closeAt = (s as SnG).close_at;
				return closeAt != null && new Date(closeAt).getTime() <= Date.now();
			});
			if (hasExpired) {
				dispatch(tournamentsListRefreshRequest());
			}
		}, 5000);
		return () => clearInterval(interval);
	}, [dispatch]);

	const normalizedTournaments = useMemo(
		() =>
			tournaments
				.map((sng: SnG) => ({
					...sng,
					winAmount: Array.isArray(sng.prices) ? sng.prices[1] : (sng.prices?.['1'] ?? 0),
				}))
				.sort((a: SnG, b: SnG) => +a.entry_size - +b.entry_size),
		[tournaments],
	);

	const presentation = useMemo(
		() =>
			normalizedTournaments.map((sng: SnG, idx: number) => {
				const trophyKey = sng.entry_icon;
				return {
					sng,
					id: String(sng.id ?? idx),
					trophyKey,
					winButtonKey: trophyKey,
					activeTables: sng.active_tables,
					winAmount: Array.isArray(sng.prices) ? sng.prices[1] : sng.prices?.['1'],
				};
			}),
		[normalizedTournaments],
	);

	// "My registered tournaments": the saga flags each list item the player is in.
	const registeredTournaments = useMemo(
		() => normalizedTournaments.filter((sng: SnG) => sng.registered),
		[normalizedTournaments],
	);
	const infoTournament = useMemo(
		() =>
			infoTournamentId == null
				? null
				: (normalizedTournaments.find((sng: SnG) => String(sng.id) === infoTournamentId) ?? null),
		[infoTournamentId, normalizedTournaments],
	);

	const glowRoom = stage.s(CARD_GLOW_ROOM);
	const wheelHeight = stage.cardHeight + glowRoom * 2;

	const carouselItems = useMemo(
		() => presentation.map(item => ({ id: item.id, sng: item.sng })),
		[presentation],
	);

	const handleActiveIndexChange = useCallback((index: number) => {
		setActiveIndex(index);
	}, []);

	const handleNavStep = useCallback((direction: -1 | 1) => {
		if (direction === 1) carouselRef.current?.next();
		else carouselRef.current?.prev();
	}, []);

	const handleDotPress = useCallback((index: number) => {
		carouselRef.current?.scrollToIndex(index);
	}, []);

	useEffect(() => {
		if (!presentation.length) return;
		setActiveIndex(current => clamp(current, 0, presentation.length - 1));
	}, [presentation.length]);

	// The desktop frame follows the VIP rooms' two columns: the tournaments on a coverflow over the
	// recent-tables bar on the left, and the registered panel over the jackpot on the right.
	if (isDesktop) {
		const d = desktop.s;
		const columns = getDesktopColumns(desktop.contentWidth, desktop.scale);
		const blockTop = Math.max(0, (desktop.contentHeight - d(DESKTOP_BLOCK_HEIGHT)) / 2);
		const cardHeight = d(DESKTOP_CARD_HEIGHT);
		const cardWidth = cardHeight / PORTRAIT_CARD_ASPECT;

		return (
			<>
				<View
					pointerEvents='box-none'
					style={[styles.desktopRoot, { paddingHorizontal: desktop.side, paddingTop: blockTop }]}
				>
					<View
						pointerEvents='box-none'
						style={[
							styles.desktopColumns,
							{ columnGap: columns.gap, height: d(DESKTOP_BLOCK_HEIGHT) },
						]}
					>
						<View style={{ width: columns.left }}>
							<View style={{ marginTop: d(DESKTOP_TOP_GAP), height: cardHeight }}>
								{loading || presentation.length === 0 ? (
									<View style={styles.tournamentsLoadingWrap}>
										{loading || !initialLoadDone ? (
											<ActivityIndicator size='large' color='#B7C1D1' />
										) : (
											<Text style={[styles.desktopEmpty, { fontSize: d(20) }]}>
												{t('T_SITE_TOURNAMENT_NO_AVAILABLE')}
											</Text>
										)}
									</View>
								) : (
									<DesktopCoverflow<{ id: string; sng: SnG }>
										items={carouselItems}
										keyOf={item => item.id}
										width={columns.left}
										cardWidth={cardWidth}
										cardHeight={cardHeight}
										scale={desktop.scale}
										initialIndex={Math.floor((carouselItems.length - 1) / 2)}
										renderItem={item => (
											<TournamentSlide
												sng={item.sng}
												cardWidth={cardWidth}
												onOpen={openTournament}
											/>
										)}
									/>
								)}
							</View>
							<View style={[styles.desktopBar, { top: d(DESKTOP_BARS_TOP) }]}>
								<DesktopRecentTablesBar
									width={columns.left}
									scale={desktop.scale}
									onPress={() => navigation.navigate(isLogged ? 'MyTables' : 'Login')}
								/>
							</View>
						</View>

						<View style={{ width: columns.right }}>
							<View style={{ marginTop: d(DESKTOP_TOP_GAP) }}>
								<RegisteredTournamentsPanel
									width={columns.right}
									title={t('T_SITE_TOURNAMENT_MY_REGISTERED', {
										defaultValue: 'MY REGISTERED TOURNAMENTS',
									})}
									emptyLabel={t('T_SITE_TOURNAMENT_NONE_REGISTERED', {
										defaultValue: 'No registered tournaments yet.',
									})}
									tournaments={registeredTournaments}
									titleOf={sng => tournamentTitle(sng, t)}
									unregisterLabel={t('T_SITE_TOURNAMENT_UNREGISTER').toUpperCase()}
									onOpen={openTournament}
									onUnregister={unregisterTournament}
									height={cardHeight}
								/>
							</View>
							<View style={[styles.desktopBar, { top: d(DESKTOP_BARS_TOP) }]}>
								<DesktopJackpotBar width={columns.right} scale={desktop.scale} />
							</View>
						</View>
					</View>
				</View>

				{infoTournament && (
					<TournamentInfoModal sng={infoTournament} onClose={() => setInfoTournamentId(null)} />
				)}
			</>
		);
	}

	return (
		<View style={styles.tournamentsRoot} pointerEvents='box-none'>
			{loading || presentation.length === 0 ? (
				<View
					style={[
						styles.block,
						styles.tournamentsLoadingWrap,
						{ top: stage.y(STAGE.cardTop), height: stage.cardHeight },
					]}
				>
					{loading || !initialLoadDone ? (
						<ActivityIndicator size='large' color='#B7C1D1' />
					) : (
						<Text style={styles.tournamentsEmpty}>{t('T_SITE_TOURNAMENT_NO_AVAILABLE')}</Text>
					)}
				</View>
			) : (
				<>
					<View
						style={[styles.block, { top: stage.y(STAGE.cardTop) - glowRoom, height: wheelHeight }]}
					>
						<TournamentsCarousel
							ref={carouselRef}
							items={carouselItems}
							activeIndex={activeIndex}
							onActiveIndexChange={handleActiveIndexChange}
							width={stage.width}
							height={wheelHeight}
							cardWidth={stage.cardWidth}
							cardHeight={stage.cardHeight}
							progress={carouselProgress}
							onOpenTournament={openTournament}
						/>
					</View>
					{presentation.length > 1 && (
						<View style={[styles.block, { top: stage.y(STAGE.dotsTop) }]}>
							<CarouselNav
								count={presentation.length}
								activeIndex={activeIndex}
								progress={carouselProgress}
								onDotPress={handleDotPress}
								onStep={handleNavStep}
								scale={stage.scale}
							/>
						</View>
					)}
				</>
			)}

			<View style={[styles.block, { top: stage.y(PANEL_TOP) }]}>
				<RegisteredTournamentsPanel
					width={stage.s(PANEL_WIDTH)}
					title={t('T_SITE_TOURNAMENT_MY_REGISTERED', {
						defaultValue: 'MY REGISTERED TOURNAMENTS',
					})}
					emptyLabel={t('T_SITE_TOURNAMENT_NONE_REGISTERED', {
						defaultValue: 'No registered tournaments yet.',
					})}
					tournaments={registeredTournaments}
					titleOf={sng => tournamentTitle(sng, t)}
					unregisterLabel={t('T_SITE_TOURNAMENT_UNREGISTER').toUpperCase()}
					onOpen={openTournament}
					onUnregister={unregisterTournament}
					height={stage.s(PANEL_HEIGHT)}
				/>
			</View>

			{infoTournament && (
				<TournamentInfoModal sng={infoTournament} onClose={() => setInfoTournamentId(null)} />
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	tournamentsRoot: {
		...StyleSheet.absoluteFillObject,
	},
	// Each block hangs at its stage y, centred across the screen.
	block: {
		position: 'absolute',
		left: 0,
		right: 0,
		alignItems: 'center',
	},
	desktopRoot: {
		...StyleSheet.absoluteFillObject,
	},
	desktopColumns: {
		flexDirection: 'row',
	},
	desktopBar: {
		position: 'absolute',
		left: 0,
	},
	desktopEmpty: {
		color: '#FFFFFF',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
	},
	tournamentsLoadingWrap: {
		width: '100%',
		height: '100%',
		alignItems: 'center',
		justifyContent: 'center',
	},
	tournamentsEmpty: {
		color: '#fff',
	},
});

export default TournamentsSection;
